import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Steps,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ExclamationCircleOutlined, LockOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { customsNationalizationAPI, NationalizationPayload } from '../services/api.ts';
import { documentTypeLabel } from '../utils/documentTypes.ts';

const { Text } = Typography;

interface NationalizationWarehouse {
  id: number;
  cod_mag?: string;
  description: string;
  stock_rows: number;
  pieces_available: number;
}

interface CandidateReservation {
  order_number: string;
  customer_name?: string;
  quantity: number;
}

interface CandidateRow {
  stock_id: number;
  product_id: number;
  product_code: string;
  client_product_code?: string;
  product_description?: string;
  dogana_code?: string;
  customer_name?: string;
  stock_code?: string;
  udc_pallet?: string;
  udc_collo?: string;
  document_type?: string;
  document_number?: string;
  document_date?: string;
  shipment_code?: string;
  vehicle_plate?: string;
  supplier_order_number?: string;
  cost?: number;
  price?: number;
  weight_gross?: number;
  qta_available: number;
  qta_reserved: number;
  qta_free: number;
  reservations: CandidateReservation[];
  blocked: boolean;
  block_reason?: string;
}

interface PreviewGroup {
  product_id: number;
  product_code?: string;
  product_description?: string;
  unit_cost?: number;
  unit_price?: number;
  quantity: number;
  source_rows: number;
  origin_documents: string[];
}

interface PreviewData {
  can_execute: boolean;
  blocks: string[];
  warnings: string[];
  groups: PreviewGroup[];
  origin_documents: { document_id: number; document_type: string; document_number: string; quantity: number }[];
  total_quantity: number;
  total_source_rows: number;
}

interface NationalizeProductsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const money = (v?: number) => (v === undefined || v === null ? '-' : `€ ${Number(v).toFixed(4)}`);

const errorDetail = (error: any, fallback: string) => error?.response?.data?.detail || fallback;

const NationalizeProductsModal: React.FC<NationalizeProductsModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [customsWarehouses, setCustomsWarehouses] = useState<NationalizationWarehouse[]>([]);
  const [nationalizedWarehouses, setNationalizedWarehouses] = useState<NationalizationWarehouse[]>([]);
  const [sourceWarehouseId, setSourceWarehouseId] = useState<number | undefined>();
  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [executing, setExecuting] = useState(false);

  // filtri del passo 2
  const [search, setSearch] = useState('');
  const [udcFilter, setUdcFilter] = useState('');
  const [documentFilter, setDocumentFilter] = useState<string | undefined>();
  const [customerFilter, setCustomerFilter] = useState<string | undefined>();
  const [plateFilter, setPlateFilter] = useState('');
  const [onlySelectable, setOnlySelectable] = useState(false);
  const [onlySelected, setOnlySelected] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setStep(0);
    setSourceWarehouseId(undefined);
    setRows([]);
    setQuantities({});
    setPreview(null);
    setSearch('');
    setUdcFilter('');
    setDocumentFilter(undefined);
    setCustomerFilter(undefined);
    setPlateFilter('');
    setOnlySelectable(false);
    setOnlySelected(false);
    form.resetFields();
    (async () => {
      try {
        setLoading(true);
        const data = await customsNationalizationAPI.getWarehouses();
        setCustomsWarehouses(data.customs || []);
        setNationalizedWarehouses(data.nationalized || []);
      } catch (error: any) {
        message.error(errorDetail(error, 'Errore nel caricamento dei magazzini'));
      } finally {
        setLoading(false);
      }
    })();
  }, [visible, form]);

  const loadCandidates = async () => {
    if (!sourceWarehouseId) return;
    try {
      setLoading(true);
      const data = await customsNationalizationAPI.getCandidates(sourceWarehouseId);
      setRows(data.rows || []);
      setQuantities({});
      setStep(1);
    } catch (error: any) {
      message.error(errorDetail(error, 'Errore nel caricamento dei prodotti'));
    } finally {
      setLoading(false);
    }
  };

  const setQuantity = (row: CandidateRow, value: number | null) => {
    setQuantities((prev) => {
      const next = { ...prev };
      const qty = Math.max(0, Math.min(Math.floor(Number(value) || 0), row.qta_free));
      if (qty > 0) next[row.stock_id] = qty;
      else delete next[row.stock_id];
      return next;
    });
  };

  const documentOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.document_number).filter(Boolean))).sort() as string[],
    [rows]
  );
  const customerOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.customer_name).filter(Boolean))).sort() as string[],
    [rows]
  );

  const filteredRows = useMemo(() => {
    const norm = (v?: string) => (v || '').toUpperCase();
    const compact = (v?: string) => norm(v).replace(/\s+/g, '');
    const s = norm(search).trim();
    const u = compact(udcFilter);
    const p = compact(plateFilter);
    return rows.filter((r) => {
      if (s) {
        const haystack = [r.product_code, r.client_product_code, r.product_description, r.stock_code, r.dogana_code]
          .map(norm)
          .join(' ');
        if (!haystack.includes(s)) return false;
      }
      if (u && !compact(r.udc_pallet).includes(u) && !compact(r.udc_collo).includes(u)) return false;
      if (p && !compact(r.vehicle_plate).includes(p) && !compact(r.shipment_code).includes(p)) return false;
      if (documentFilter && r.document_number !== documentFilter) return false;
      if (customerFilter && r.customer_name !== customerFilter) return false;
      if (onlySelectable && r.blocked) return false;
      if (onlySelected && !quantities[r.stock_id]) return false;
      return true;
    });
  }, [rows, search, udcFilter, plateFilter, documentFilter, customerFilter, onlySelectable, onlySelected, quantities]);

  const totals = useMemo(() => {
    const selected = rows.filter((r) => quantities[r.stock_id]);
    return {
      rows: selected.length,
      products: new Set(selected.map((r) => r.product_id)).size,
      pieces: selected.reduce((sum, r) => sum + (quantities[r.stock_id] || 0), 0),
    };
  }, [rows, quantities]);

  const buildPayload = (withDocument: boolean): NationalizationPayload => {
    const payload: NationalizationPayload = {
      source_warehouse_id: sourceWarehouseId as number,
      lines: Object.entries(quantities).map(([stockId, quantity]) => ({ stock_id: Number(stockId), quantity })),
    };
    if (withDocument) {
      const values = form.getFieldsValue();
      payload.destination_warehouse_id = values.destination_warehouse_id;
      payload.document_number = (values.document_number || '').trim().toUpperCase();
      payload.document_date = values.document_date ? values.document_date.format('YYYY-MM-DD') : undefined;
      payload.mnr_code = values.mnr_code || undefined;
      payload.notes = values.notes || undefined;
    }
    return payload;
  };

  const goToDocumentStep = async () => {
    try {
      setLoading(true);
      const data = await customsNationalizationAPI.preview(buildPayload(false));
      setPreview(data);
      setStep(2);
    } catch (error: any) {
      message.error(errorDetail(error, "Errore nell'anteprima dello sdoganamento"));
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    try {
      setExecuting(true);
      const result = await customsNationalizationAPI.execute(buildPayload(true));
      Modal.success({
        title: 'Prodotti sdoganati',
        width: 560,
        content: (
          <div>
            <p>{result.message}</p>
            <p>
              Documento H1 (IM4) <b>{result.document?.document_number}</b>: {result.document?.items_created} righe,{' '}
              {result.total_quantity} pezzi scaricati da {result.source_rows} giacenze del magazzino doganale.
            </p>
            {(result.warnings || []).map((w: string) => (
              <p key={w}>
                <Text type="warning">{w}</Text>
              </p>
            ))}
          </div>
        ),
      });
      onSuccess();
    } catch (error: any) {
      Modal.error({
        title: 'Sdoganamento non eseguito',
        width: 640,
        content: (
          <div>
            {String(errorDetail(error, 'Errore durante lo sdoganamento'))
              .split(' | ')
              .map((line) => (
                <p key={line}>{line}</p>
              ))}
            <p>
              <Text type="secondary">Nessuna modifica è stata applicata. Correggere i dati e riprovare.</Text>
            </p>
          </div>
        ),
      });
    } finally {
      setExecuting(false);
    }
  };

  const confirmAndExecute = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    const values = form.getFieldsValue();
    const destination = nationalizedWarehouses.find((w) => w.id === values.destination_warehouse_id);
    Modal.confirm({
      title: 'Confermare lo sdoganamento definitivo?',
      icon: <ExclamationCircleOutlined style={{ color: '#fa541c' }} />,
      width: 600,
      content: (
        <div>
          <p>
            Stai per sdoganare <b>{totals.pieces} pezzi</b> ({totals.products} prodotti, {totals.rows} giacenze) con il
            documento H1 (IM4) <b>{(values.document_number || '').trim().toUpperCase()}</b> verso il magazzino{' '}
            <b>{destination?.description}</b>.
          </p>
          <Alert
            type="error"
            showIcon
            message="Azione definitiva e non annullabile"
            description="I prodotti verranno scaricati dal magazzino doganale e dalle bollette con cui sono entrati, e caricati nel magazzino nazionalizzato con il nuovo H1 (IM4). Non esiste una funzione per annullare l'operazione: controlla con attenzione prodotti, quantità, codice e data H1 (IM4) prima di confermare."
          />
        </div>
      ),
      okText: 'Sì, sdogana definitivamente',
      okButtonProps: { danger: true },
      cancelText: 'Torna indietro',
      onOk: execute,
    });
  };

  const columns: ColumnsType<CandidateRow> = [
    {
      title: 'Prodotto',
      key: 'product',
      width: 300,
      fixed: 'left',
      render: (_, r) => (
        <div>
          <Text strong>{r.product_code}</Text>
          {r.client_product_code && <Text type="secondary"> · {r.client_product_code}</Text>}
          <div style={{ fontSize: 12, color: '#666' }}>{r.product_description}</div>
        </div>
      ),
    },
    { title: 'Cliente', dataIndex: 'customer_name', width: 140, render: (v) => v || '-' },
    { title: 'Partita', dataIndex: 'stock_code', width: 100, render: (v) => v || '-' },
    { title: 'UDC Pallet', dataIndex: 'udc_pallet', width: 130, render: (v) => v || '-' },
    { title: 'UDC Collo', dataIndex: 'udc_collo', width: 150, render: (v) => v || '-' },
    {
      title: 'Bolletta di entrata',
      key: 'document',
      width: 190,
      render: (_, r) =>
        r.document_number ? (
          <div>
            <Tag color="blue">{documentTypeLabel(r.document_type)}</Tag>
            {r.document_number}
            {r.document_date && (
              <div style={{ fontSize: 12, color: '#666' }}>{dayjs(r.document_date).format('DD/MM/YYYY')}</div>
            )}
          </div>
        ) : (
          '-'
        ),
    },
    { title: 'Targa / Container', dataIndex: 'vehicle_plate', width: 130, render: (v) => v || '-' },
    { title: 'Cod. Doganale', dataIndex: 'dogana_code', width: 110, render: (v) => v || '-' },
    { title: 'Costo', dataIndex: 'cost', width: 95, align: 'right', render: (v) => money(v) },
    { title: 'Vendita', dataIndex: 'price', width: 95, align: 'right', render: (v) => money(v) },
    { title: 'Disponibile', dataIndex: 'qta_available', width: 95, align: 'right' },
    {
      title: 'Prenotato',
      dataIndex: 'qta_reserved',
      width: 110,
      align: 'right',
      render: (v: number, r) =>
        v > 0 ? (
          <Tooltip
            title={
              <div>
                {r.reservations.map((x) => (
                  <div key={x.order_number}>
                    Ordine {x.order_number}
                    {x.customer_name ? ` (${x.customer_name})` : ''}: {x.quantity} pz
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="orange">Prenotato {v}</Tag>
          </Tooltip>
        ) : (
          <Text type="secondary">0</Text>
        ),
    },
    {
      title: 'Libero',
      dataIndex: 'qta_free',
      width: 80,
      align: 'right',
      render: (v: number, r) => <Text strong={!r.blocked}>{v}</Text>,
    },
    {
      title: 'Q.tà da sdoganare',
      key: 'quantity',
      width: 190,
      fixed: 'right',
      render: (_, r) =>
        r.blocked ? (
          <Tooltip title={`${r.block_reason}: la merce prenotata non si può sdoganare`}>
            <Space size={4}>
              <InputNumber disabled style={{ width: 90 }} />
              <Tag icon={<LockOutlined />} color="red">
                Bloccato
              </Tag>
            </Space>
          </Tooltip>
        ) : (
          <Space size={4}>
            <InputNumber
              min={0}
              max={r.qta_free}
              precision={0}
              style={{ width: 90 }}
              value={quantities[r.stock_id] ?? null}
              onChange={(value) => setQuantity(r, value)}
            />
            <Button size="small" onClick={() => setQuantity(r, r.qta_free)}>
              Tutto
            </Button>
          </Space>
        ),
    },
  ];

  const previewColumns: ColumnsType<PreviewGroup> = [
    {
      title: 'Prodotto (riga del nuovo H1)',
      key: 'product',
      render: (_, g) => (
        <div>
          <Text strong>{g.product_code}</Text>
          <div style={{ fontSize: 12, color: '#666' }}>{g.product_description}</div>
        </div>
      ),
    },
    { title: 'Costo', dataIndex: 'unit_cost', width: 110, align: 'right', render: (v) => money(v) },
    { title: 'Vendita', dataIndex: 'unit_price', width: 110, align: 'right', render: (v) => money(v) },
    { title: 'Quantità', dataIndex: 'quantity', width: 100, align: 'right', render: (v) => <Text strong>{v}</Text> },
    { title: 'Giacenze di origine', dataIndex: 'source_rows', width: 140, align: 'right' },
    {
      title: 'Bollette scaricate',
      dataIndex: 'origin_documents',
      render: (docs: string[]) => docs.map((d) => <Tag key={d}>{d}</Tag>),
    },
  ];

  const sourceWarehouse = customsWarehouses.find((w) => w.id === sourceWarehouseId);

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button onClick={onCancel} disabled={executing}>
        Annulla
      </Button>
      <Space>
        {step > 0 && (
          <Button onClick={() => setStep(step - 1)} disabled={executing || loading}>
            Indietro
          </Button>
        )}
        {step === 0 && (
          <Button type="primary" disabled={!sourceWarehouseId} loading={loading} onClick={loadCandidates}>
            Avanti
          </Button>
        )}
        {step === 1 && (
          <Button type="primary" disabled={totals.pieces === 0} loading={loading} onClick={goToDocumentStep}>
            Avanti ({totals.pieces} pz)
          </Button>
        )}
        {step === 2 && (
          <Button
            type="primary"
            danger
            loading={executing}
            disabled={!preview || !preview.can_execute}
            onClick={confirmAndExecute}
          >
            Sdogana prodotti
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <Modal
      title="Nazionalizza Prodotti"
      open={visible}
      onCancel={executing ? undefined : onCancel}
      width={step === 1 ? '96%' : 1000}
      style={{ top: 20 }}
      footer={footer}
      maskClosable={false}
      destroyOnClose
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 20 }}
        items={[{ title: 'Magazzino doganale' }, { title: 'Prodotti e quantità' }, { title: 'Documento H1 (IM4)' }]}
      />

      {step === 0 && (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Sdoganamento di merce già presente in un magazzino doganale"
            description="Scegli il magazzino doganale da cui prelevare i prodotti. Nel passo successivo indicherai prodotti e quantità, poi i dati del documento H1 (IM4) rilasciato dalla dogana e il magazzino nazionalizzato di destinazione."
          />
          <Select
            style={{ width: '100%' }}
            size="large"
            placeholder="Seleziona il magazzino doganale"
            loading={loading}
            value={sourceWarehouseId}
            onChange={setSourceWarehouseId}
            options={customsWarehouses.map((w) => ({
              value: w.id,
              label: `${w.cod_mag ? w.cod_mag + ' - ' : ''}${w.description} (${w.stock_rows} giacenze, ${w.pieces_available} pz disponibili)`,
              disabled: w.stock_rows === 0,
            }))}
          />
        </div>
      )}

      {step === 1 && (
        <div>
          <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={6}>
              <Input
                allowClear
                placeholder="Codice, descrizione, partita, cod. doganale"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col xs={12} md={3}>
              <Input allowClear placeholder="UDC pallet / collo" value={udcFilter} onChange={(e) => setUdcFilter(e.target.value)} />
            </Col>
            <Col xs={12} md={4}>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="Bolletta di entrata"
                value={documentFilter}
                onChange={setDocumentFilter}
                options={documentOptions.map((d) => ({ value: d, label: d }))}
              />
            </Col>
            <Col xs={12} md={3}>
              <Input allowClear placeholder="Targa / container" value={plateFilter} onChange={(e) => setPlateFilter(e.target.value)} />
            </Col>
            <Col xs={12} md={3}>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="Cliente"
                value={customerFilter}
                onChange={setCustomerFilter}
                options={customerOptions.map((c) => ({ value: c, label: c }))}
              />
            </Col>
            <Col xs={24} md={5}>
              <Space>
                <Checkbox checked={onlySelectable} onChange={(e) => setOnlySelectable(e.target.checked)}>
                  Solo sdoganabili
                </Checkbox>
                <Checkbox checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)}>
                  Solo selezionati
                </Checkbox>
              </Space>
            </Col>
          </Row>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 16px',
              marginBottom: 12,
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
            }}
          >
            <Text>
              Magazzino: <b>{sourceWarehouse?.description}</b> · {filteredRows.length} giacenze mostrate su {rows.length}
            </Text>
            <Space size={32}>
              <Statistic title="Giacenze selezionate" value={totals.rows} valueStyle={{ fontSize: 18 }} />
              <Statistic title="Prodotti" value={totals.products} valueStyle={{ fontSize: 18 }} />
              <Statistic title="Pezzi da sdoganare" value={totals.pieces} valueStyle={{ fontSize: 18, color: '#389e0d' }} />
              <Button size="small" disabled={totals.rows === 0} onClick={() => setQuantities({})}>
                Azzera selezione
              </Button>
            </Space>
          </div>

          <Table<CandidateRow>
            rowKey="stock_id"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={filteredRows}
            scroll={{ x: 1900, y: 'calc(100vh - 430px)' }}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200], showTotal: (t) => `${t} giacenze` }}
            rowClassName={(r) => (quantities[r.stock_id] ? 'ant-table-row-selected' : '')}
          />
        </div>
      )}

      {step === 2 && (
        <div>
          <Form form={form} layout="vertical" initialValues={{ document_date: dayjs() }}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="document_number"
                  label="Codice H1 (IM4)"
                  rules={[{ required: true, whitespace: true, message: 'Inserire il codice H1 (IM4) rilasciato dalla dogana' }, { max: 50 }]}
                >
                  <Input placeholder="Codice del documento H1 (IM4)" maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="document_date" label="Data H1 (IM4)" rules={[{ required: true, message: 'Inserire la data H1 (IM4)' }]}>
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item
                  name="destination_warehouse_id"
                  label="Magazzino nazionalizzato di destinazione"
                  rules={[{ required: true, message: 'Scegliere il magazzino di destinazione' }]}
                >
                  <Select
                    placeholder="Seleziona il magazzino"
                    options={nationalizedWarehouses.map((w) => ({
                      value: w.id,
                      label: `${w.cod_mag ? w.cod_mag + ' - ' : ''}${w.description}`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="mnr_code" label="MRN (facoltativo)">
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item name="notes" label="Note (facoltative)">
                  <Input.TextArea rows={1} autoSize={{ minRows: 1, maxRows: 3 }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {preview && preview.blocks.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Lo sdoganamento non può essere eseguito"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {preview.blocks.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              }
            />
          )}
          {preview &&
            preview.warnings.map((w) => <Alert key={w} type="warning" showIcon style={{ marginBottom: 12 }} message={w} />)}

          {preview && (
            <>
              <Text strong>
                Riepilogo: {preview.total_quantity} pezzi da {preview.total_source_rows} giacenze → {preview.groups.length}{' '}
                righe nel nuovo H1 (IM4) (prodotti uguali accorpati, senza UDC; righe separate se costo o vendita sono diversi)
              </Text>
              <Table<PreviewGroup>
                style={{ marginTop: 8 }}
                rowKey={(g) => `${g.product_id}-${g.unit_cost}-${g.unit_price}-${g.origin_documents.join(',')}`}
                size="small"
                columns={previewColumns}
                dataSource={preview.groups}
                pagination={false}
                scroll={{ y: 260 }}
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Bollette di origine scaricate: </Text>
                {preview.origin_documents.map((d) => (
                  <Tag key={d.document_id}>
                    {documentTypeLabel(d.document_type)} {d.document_number}: {d.quantity} pz
                  </Tag>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  Costo e prezzo di vendita restano quelli attuali; dopo lo sdoganamento si possono modificare dal pulsante
                  "Gestisci Prodotti" del nuovo H1 (IM4).
                </Text>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default NationalizeProductsModal;
